/**
 * Supabase client construction (Phase 5b, step 4).
 *
 * A lazily-constructed singleton built from the two env vars gathered in
 * step 1. `supabaseConfigured` is a build-time constant (Vite inlines
 * import.meta.env), so the composition root can branch on it and Rollup can
 * tree-shake the unused path: a build with no Supabase env vars set drops
 * this module and @supabase/supabase-js from the bundle entirely.
 *
 * The publishable key (`sb_publishable_...`) goes here as the second arg to
 * createClient. It's client-safe by design; row-level security on the
 * `scenarios` table is the actual access control.
 */
import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';
import { BW_SUPABASE_URL, BW_SUPABASE_PUBLISHABLE_KEY } from '../buildEnv';

const url = BW_SUPABASE_URL;
const key = BW_SUPABASE_PUBLISHABLE_KEY;

/** True when both env vars are present — i.e. the app should use Supabase. */
export const supabaseConfigured = Boolean(url && key);

let client: SupabaseClient | null = null;

/**
 * Get the Supabase client singleton. Throws if the env vars aren't set — but
 * callers only reach here when `supabaseConfigured` is true, so that's a
 * programming error, not a runtime condition users hit.
 */
export function getSupabaseClient(): SupabaseClient {
  if (!client) {
    if (!url || !key) {
      throw new Error(
        'Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY.',
      );
    }
    client = createClient(url, key, {
      auth: {
        // Persist the session in localStorage and refresh it automatically so
        // a signed-in author stays signed in across reloads.
        persistSession: true,
        autoRefreshToken: true,
        // Magic-link callbacks arrive as URL hash params; let the client
        // pick them up on load.
        detectSessionInUrl: true,
      },
    });
  }
  return client;
}

// ---------- Auth helpers (Phase 5b, step 6) ----------
// Thin wrappers so the rest of the app touches Supabase auth in exactly one
// place. All assume `supabaseConfigured` is true (callers only reach them in
// cloud mode, behind the AuthGate).

/**
 * Send a magic-link sign-in email. The link returns the user to this app's
 * origin, which must be in the project's redirect allowlist (set in step 3).
 */
export async function sendMagicLink(email: string): Promise<void> {
  const { error } = await getSupabaseClient().auth.signInWithOtp({
    email,
    options: { emailRedirectTo: window.location.origin },
  });
  if (error) throw error;
}

/** Sign the current user out. onAuthChange subscribers see the null session. */
export async function signOut(): Promise<void> {
  await getSupabaseClient().auth.signOut();
}

/** Read the current session (local, no network unless a refresh is due). */
export async function getCurrentSession(): Promise<Session | null> {
  const { data } = await getSupabaseClient().auth.getSession();
  return data.session;
}

/** The signed-in user's email, if any. */
export async function getCurrentUserEmail(): Promise<string | null> {
  const session = await getCurrentSession();
  return session?.user?.email ?? null;
}

/**
 * Subscribe to auth state changes (sign-in, sign-out, token refresh). Returns
 * an unsubscribe function.
 */
export function onAuthChange(cb: (session: Session | null) => void): () => void {
  const { data } = getSupabaseClient().auth.onAuthStateChange((_event, session) => {
    cb(session);
  });
  return () => data.subscription.unsubscribe();
}
